import { useEffect, useState } from "react"
import useLazyLoading from "../../common/hooks/loader";
import Loader from "../../components/loader";
import { Box, Button, Card, Divider, Grid, InputLabel, MenuItem, OutlinedInput, Select, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import GridContent from "../../components/grid";
import { IHeaderGrid } from "../../interfaces/grid/IHeaderGrid";
import GridHeader from "../../components/grid/header";
import { IPortfolioBalancingDto } from "../../interfaces/portfolio-balancing/IPortfolioBalancingDto";
import { useNotification } from "../../components/notification/useNotification";
import { tryGetPortfolioBalancingByWalletId, tryUpdateDesiredPercentageList } from "../../services/portfolio-balancing/portfolio-balancing.service";
import extractErrorMessage from "../../common/utils/errorMessages";
import { formatPriceToDolar } from "../../common/utils/number";
import { colorByValue } from "../../common/utils/color";
import { ISimplePortfolioBalancingDto } from "../../interfaces/portfolio-balancing/ISimplePortfolioBalancingDto";
import IWalletDto from "../../interfaces/wallet/IWalletDto";
import { tryGetAllBalancedWallet } from "../../services/wallet/wallet-service";
import CryptoPriceSlider from "../../components/crypto-price-slider";

const headerColumnList: IHeaderGrid[] = [
    {
        align: 'left',
        name: 'Cripto Moeda',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Perc% Pretendida',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Perc% Actual',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Qtd. Desejada',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Qtd. Actual',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Diferença Qtd.',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Diferença de Valor',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Valor Actual',
        padding: 'normal'
    },
    {
        align: 'left',
        name: 'Preço Actual',
        padding: 'normal'
    },
    {
        align: 'right',
        name: 'Preço Médio',
        padding: 'normal'
    },
];


export default function PortfolioBalancing() {
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();
    const [portfolioBalancingList, setPortfolioBalancingList] = useState<IPortfolioBalancingDto[]>([]);
    const [balancedWalletList, setBalancedWalletList] = useState<IWalletDto[]>([]);
    const [walletId, setWalletId] = useState<string>("");
    const [totalPercentage, setTotalPercentage] = useState<number>(0);
    const [riskPercentage, setRiskPercentage] = useState<number>(0);
    const [stablePercentage, setStablePercentage] = useState<number>(0);
    const [differencePercentage, setDifferencePercentage] = useState<number>(0);

    async function getBalancedWalletList() {
        try {
            var response = await tryGetAllBalancedWallet();

            if (response.status === 200) {
                setBalancedWalletList(response.data);
                setWalletId(response.data[0].id);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
            return [];
        }
    }

    async function getPortfolioBalancingList(wallet: string) {
        try {
            var response = await tryGetPortfolioBalancingByWalletId(wallet);

            if (response.status === 200) {
                setPortfolioBalancingList(response.data);
                setPercentages(response.data, false);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
    }

    useEffect(() => {
        setLoading(true);

        if (!walletId) {
            getBalancedWalletList();
        }

        if (walletId) {
            getPortfolioBalancingList(walletId);
        }

        setLoading(false);

    }, [walletId])

    const percentageHandleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, portfolioBalancing: IPortfolioBalancingDto) => {
        const inputValue = parseFloat(event.target.value);

        const updatedPortfolioList = portfolioBalancingList.map(portfolio =>
            portfolio.id === portfolioBalancing.id ? { ...portfolio, desiredPercentage: inputValue } : portfolio
        );

        setPortfolioBalancingList(updatedPortfolioList);
        setPercentages(updatedPortfolioList, true);
    };

    const setPercentages = (portfolioList: IPortfolioBalancingDto[], isChange: boolean) => {
        const totalPerc = parseFloat((portfolioList.reduce((acc, item) => acc + (isChange ? item.desiredPercentage : item.actualPercentage), 0)).toFixed(2));
        const diff = parseFloat((100 - totalPerc).toFixed(2));

        setTotalPercentage(totalPerc);
        setRiskPercentage(parseFloat(portfolioList.filter(x => x.isStable === false).reduce((acc, item) => acc + (isChange ? item.desiredPercentage : item.actualPercentage), 0).toFixed(2)));
        setStablePercentage(parseFloat(portfolioList.filter(x => x.isStable).reduce((acc, item) => acc + (isChange ? item.desiredPercentage : item.actualPercentage), 0).toFixed(2)));
        setDifferencePercentage(diff);   
    }

    const handleClickSave = async () => {
        const simplePortfolioBalancingList: ISimplePortfolioBalancingDto[] = [];

        portfolioBalancingList.forEach(portfolioBalancing => {
            const simplePortfolio: ISimplePortfolioBalancingDto = {
                id: portfolioBalancing.id,
                cryptoCurrencyId: portfolioBalancing.cryptoCurrencyId,
                desiredPercentage: portfolioBalancing.desiredPercentage
            }

            simplePortfolioBalancingList.push(simplePortfolio);
        });

        const totalPercentage = parseFloat(simplePortfolioBalancingList.reduce((acc, item) => acc + item.desiredPercentage, 0).toFixed(2));

        if (totalPercentage > 100) {
            displayNotification({ type: 'error', message: 'Não é possível guardar uma percentagem superior a 100%' });
            return;
        }

        try {
            setLoading(true);

            var response = await tryUpdateDesiredPercentageList(simplePortfolioBalancingList);

            if (response.status === 200) {
                displayNotification({ type: 'success', message: 'Percentagens actualizadas com sucesso' });
                getPortfolioBalancingList(walletId);
            }
        }
        catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
        finally {
            setLoading(false);
        }
    }

    return (
        <>
            {showLoading ? <Loader /> : false}
            <Box mt={3} mb={1}>
                <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                    Balanceamento de Portfolio
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Analisa e ajusta a distribuição do teu portfolio
                </Typography>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
            </Box>
            <Box marginTop={3} marginBottom={3}>
                <CryptoPriceSlider />
            </Box>
            <Box>
                <Grid container justifyContent="space-between" alignItems="flex-end" spacing={3}>
                    <Grid>
                        <InputLabel id="portfolio-balancing-label" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Carteira
                        </InputLabel>
                        <Select
                            labelId="portfolio-balancing-label"
                            value={walletId}
                            size="small"
                            onChange={(event) => setWalletId(event.target.value)}
                            MenuProps={{ disableScrollLock: true }}
                        >
                            {balancedWalletList && balancedWalletList.map((balancedWallet) => (
                                <MenuItem
                                    key={balancedWallet.id}
                                    value={balancedWallet.id}
                                >
                                    {balancedWallet.walletName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid>
                        <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Percentagem Total
                        </InputLabel>
                        <OutlinedInput
                            type="number"
                            value={totalPercentage}
                            size="small"
                            disabled
                        />
                    </Grid>
                    <Grid>
                        <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Percentagem Risco
                        </InputLabel>
                        <OutlinedInput
                            type="number"
                            value={riskPercentage}
                            size="small"
                            disabled
                        />
                    </Grid>
                    <Grid>
                        <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Percentagem Estável
                        </InputLabel>
                        <OutlinedInput
                            type="number"
                            value={stablePercentage}
                            size="small"
                            disabled
                        />
                    </Grid>
                    <Grid>
                        <InputLabel sx={{ textTransform: 'uppercase', fontSize: '0.75rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                            Diferença
                        </InputLabel>
                        <OutlinedInput
                            type="number"
                            value={differencePercentage}
                            size="small"
                            disabled
                        />
                    </Grid>
                    <Grid alignSelf="flex-end">
                        <Button type="submit" variant="contained" color="primary" onClick={handleClickSave}>
                            Guardar Percentagens
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <Box>
                    <GridContent>
                        <GridHeader headerProps={headerColumnList} />
                        <TableBody>
                            {portfolioBalancingList && portfolioBalancingList.map((portfolioBalancing, index) => (
                                <TableRow
                                    key={portfolioBalancing.id}
                                    sx={{
                                        backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell align="left">
                                        <Grid container alignItems="center" spacing={0.5}>
                                            <Grid>
                                                <img src={portfolioBalancing.cryptoCurrencyUrl} width="15" height="15" />
                                            </Grid>
                                            <Grid>
                                                <strong>{portfolioBalancing.cryptoCurrencyName}</strong>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <OutlinedInput
                                                type="number"
                                                value={portfolioBalancing.desiredPercentage}
                                                size="small"
                                                onChange={(event) => percentageHandleOnChange(event, portfolioBalancing)}
                                            />
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography>{portfolioBalancing.actualPercentage}%</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography>{portfolioBalancing.desiredQuantityCryptoCurrency}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography>{portfolioBalancing.quantityCryptoCurrency}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography color={colorByValue(portfolioBalancing.differenceQuantity)} >{portfolioBalancing.differenceQuantity}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography color={colorByValue(portfolioBalancing.differenceAmount)}>{formatPriceToDolar(portfolioBalancing.differenceAmount)}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography>{formatPriceToDolar(portfolioBalancing.totalAmount)}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Grid>
                                            <Typography>{formatPriceToDolar(portfolioBalancing.actualPrice)}</Typography>
                                        </Grid>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Grid>
                                            <Typography>{formatPriceToDolar(portfolioBalancing.averagePrice)}</Typography>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </GridContent>
                </Box>
            </Card>
        </>
    )
}